import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import apiClient from '../../api/apiClient';
import {
  ArrowLeft,
  Edit,
  User,
  Users,
  GraduationCap,
  Briefcase,
  Award,
  Shield,
  Key,
} from 'lucide-react';
import { containerVariants, itemVariants } from '../../utils/animations';
import { useToast } from '../../context/ToastContext';
import { createPageUrl } from '../../utils';

import PersonalDetailTab from '../../components/hris/employee/detail/PersonalDetailTab';
import FamilyDetailTab from '../../components/hris/employee/detail/FamilyDetailTab';
import EducationDetailTab from '../../components/hris/employee/detail/EducationDetailTab';
import EmploymentDetailTab from '../../components/hris/employee/detail/EmploymentDetailTab';
import CertificationDetailTab from '../../components/hris/employee/detail/CertificationDetailTab';
import AccountDetailTab from '../../components/hris/employee/detail/AccountDetailTab';
import PunishmentDetailTab from '../../components/hris/employee/detail/PunishmentDetailTab';

const TABS = [
  { id: 'personal', label: 'Data Pribadi', icon: User },
  { id: 'family', label: 'Keluarga', icon: Users },
  { id: 'education', label: 'Pendidikan', icon: GraduationCap },
  { id: 'employment', label: 'Kepegawaian', icon: Briefcase },
  { id: 'certification', label: 'Sertifikasi', icon: Award },
  { id: 'account', label: 'Akun Login', icon: Key },
  { id: 'punishment', label: 'Punishment', icon: Shield },
];

export default function EmployeeDetail() {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [searchParams] = useSearchParams();
  const employeeId = searchParams.get('id');

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('personal');
  const [employeeData, setEmployeeData] = useState<any>(null);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [employees, setEmployees] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [empDetail, deptData, posData, empData] = await Promise.all([
        apiClient.get(`/hris/employees/${employeeId}`),
        apiClient.get('/hris/departments'),
        apiClient.get('/hris/positions'),
        apiClient.get('/hris/employees'),
      ]);

      const depts = deptData.departments || deptData || [];
      const poss = posData.positions || posData || [];
      const emps = empData.employees || empData || [];

      setEmployeeData(empDetail);
      setDepartments(depts);
      setPositions(poss);
      setEmployees(emps);
    } catch (error: any) {
      showError('Gagal memuat data pegawai');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [employeeId, showError, navigate]);

  useEffect(() => {
    if (employeeId) {
      loadData();
    } else {
      showError('ID pegawai tidak ditemukan');
      navigate(-1);
    }
  }, [employeeId, loadData, navigate, showError]);

  const getDepartmentName = (deptId: number | null | undefined) => {
    if (!deptId) return '-';
    const dept = departments.find((d: any) => d.id === deptId);
    return dept?.name || '-';
  };

  const getPositionName = (posId: number | null | undefined) => {
    if (!posId) return '-';
    const pos = positions.find((p: any) => p.id === posId);
    return pos?.name || '-';
  };

  const getEmployeeName = (empId: number | null | undefined) => {
    if (!empId) return '-';
    const emp = employees.find((e: any) => e.id === empId);
    return emp?.full_name || '-';
  };

  const renderTabContent = () => {
    if (!employeeData) return null;

    switch (activeTab) {
      case 'personal':
        return <PersonalDetailTab employeeData={employeeData} />;
      case 'family':
        return <FamilyDetailTab employeeData={employeeData} />;
      case 'education':
        return <EducationDetailTab employeeData={employeeData} />;
      case 'employment':
        return (
          <EmploymentDetailTab
            employeeData={employeeData}
            getDepartmentName={getDepartmentName}
            getPositionName={getPositionName}
            getEmployeeName={getEmployeeName}
          />
        );
      case 'certification':
        return <CertificationDetailTab employeeData={employeeData} />;
      case 'account':
        return <AccountDetailTab employeeData={employeeData} employeeId={employeeId} />;
      case 'punishment':
        return <PunishmentDetailTab employeeId={employeeId} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!employeeData) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Data pegawai tidak ditemukan</p>
      </div>
    );
  }

  return (
    <motion.div
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Detail Pegawai</h1>
            <p className="text-gray-500 mt-1">Lihat informasi lengkap data pegawai</p>
          </div>
        </div>
        <button
          onClick={() => {
            window.location.href = createPageUrl(`EmployeeForm?id=${employeeId}`);
          }}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 active:scale-95 flex items-center gap-2 transition-all duration-300"
        >
          <Edit className="w-4 h-4" />
          Edit Pegawai
        </button>
      </motion.div>

      {/* Tabs */}
      <motion.div
        variants={itemVariants}
        className="bg-gray-100 p-1 rounded-lg flex flex-wrap gap-1 mb-6"
      >
        {TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
              activeTab === tab.id
                ? 'bg-white text-emerald-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </motion.div>

      {/* Tab Content */}
      <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm border p-6">
        {renderTabContent()}
      </motion.div>
    </motion.div>
  );
}
